'use client';

import classNames from 'classnames';
import NextImage, { ImageLoader } from 'next/image';
import { type SyntheticEvent, useCallback, useState } from 'react';
import getRgbDataUrl from '../../utils/getRgbDataUrl';
import ShimmerContainer from '../ShimmerContainer';
import styles from './styles.module.scss';

type ImageProps = {
  id?: string;
  src: any;
  className?: string;
  loader?: ImageLoader;
  alt: string;
  title?: string;
  height?: number;
  width?: number;
  onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  priority?: boolean;
  quality?: number;
  fill?: boolean;
  sizes?: string;
  placeholder?: 'blur' | 'empty' | undefined;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  isLcp?: boolean;
  layout?: string;
  itemProp?: string;
  handleLoadingState?: boolean;
  blurDataURL?: string;
};

const Image = ({
  id,
  src,
  className,
  loader,
  alt,
  title,
  height,
  width,
  onError,
  priority = false,
  quality,
  loading,
  onLoad,
  fill,
  placeholder = 'blur',
  sizes,
  unoptimized,
  isLcp = false,
  layout,
  itemProp,
  handleLoadingState,
  blurDataURL
}: ImageProps) => {
  const [ready, setReady] = useState(false);
  const [didError, setDidError] = useState(false);

  const baseStyles = isLcp
    ? {} // To avoid delay on displaying LCP
    : {
        opacity: ready ? 1 : handleLoadingState ? 0 : 0.2
      };

  const defaultOnError = useCallback(() => {
    setDidError(true);
  }, []);

  if (!src || didError) {
    return (
      <div className={className}>
        <div className={styles.emptyImageBackground} />
      </div>
    );
  }

  const ImageComponent = (
    <NextImage
      className={classNames({ [styles.nonLcpImage]: !isLcp }, className)}
      id={id}
      style={baseStyles}
      src={src}
      height={height}
      alt={alt}
      title={title}
      placeholder={placeholder}
      blurDataURL={blurDataURL ?? getRgbDataUrl(234, 241, 228)}
      width={width}
      onError={onError ?? defaultOnError}
      priority={isLcp || priority}
      quality={quality}
      fill={fill}
      loading={isLcp ? 'eager' : loading}
      sizes={sizes}
      onLoad={e => {
        setReady(true);
        onLoad?.(e);
      }}
      loader={loader}
      // Many of our images coming from /api/alltrails are already optimized and this lets us use relative paths for those sources
      unoptimized={(src && typeof src === 'string' && src.startsWith('/api/alltrails/')) || unoptimized}
      layout={layout}
      itemProp={itemProp}
      {...(isLcp && {
        fetchPriority: 'high' // regression in next 15 https://github.com/vercel/next.js/issues/74234#issuecomment-2775594665
      })}
    />
  );

  if (handleLoadingState && !ready) {
    return <ShimmerContainer className={styles.loading}>{ImageComponent}</ShimmerContainer>;
  }

  return ImageComponent;
};

export default Image;
